import React, { memo } from "react"
import { Loader } from "~/ui-rtk/components/ui/common"
import { useConnect } from "./connect"

import { renderStatusIcon, type ConnectionStatus } from "../../utils"

type TConnectorStatusProps = {
  companyConnectorId: string
  connectorStatus?: string // Optional prop to override the status display
  hasError?: boolean // Whether the connector has an error
}

const ConnectorStatus: React.FC<TConnectorStatusProps> = ({
  companyConnectorId,
  connectorStatus,
  hasError = false,
}) => {
  const { connectorInfo, isLoading } = useConnect(companyConnectorId)

  if (isLoading) {
    // TODO: Add lazy loader
    return <Loader />
  }

  // Determine what status text and icon to show
  const getStatusText = () => {
    if (hasError) {
      return "Unhealthy"
    }
    if (connectorStatus === "UPGRADE") {
      return "Upgrade needed"
    }
    if (
      connectorStatus === "PENDING_SETUP" ||
      connectorInfo?.status === "pending_setup"
    ) {
      return "Pending setup"
    }
    // Capitalize the status for display
    if (connectorInfo?.status === "healthy") {
      return "Healthy"
    }
    if (connectorInfo?.status === "unhealthy") {
      return "Unhealthy"
    }
    return connectorInfo?.status || "unknown"
  }

  const getStatusIcon = (): ConnectionStatus => {
    if (hasError) {
      return "error" // Show error icon when there's an error
    }
    if (connectorStatus === "UPGRADE") {
      return "warning" // Use warning icon for upgrade needed
    }
    if (connectorStatus === "PENDING_SETUP") {
      return "pending_setup" // Use warning icon for pending setup
    }
    // connectorInfo.status is already a ConnectionStatus type ("healthy" | "unhealthy" | "pending_setup")
    return !connectorInfo?.status
      ? "unhealthy"
      : (connectorInfo.status as ConnectionStatus)
  }

  const renderStatusIndicator = () => renderStatusIcon(getStatusIcon())

  return (
    <article className="max-w-89 w-full mb-2 space-y-5">
      <div className="flex  flex-col items-start justify-start gap-2">
        <div className="flex items-center justify-start gap-2">
          {renderStatusIndicator()}
          <b className="text-4 leading-7 font-bold text-platinum">Status:</b>
          <p className="text-4 leading-7 font-medium text-platinum">
            {getStatusText()}
          </p>
        </div>
      </div>
    </article>
  )
}

export default memo(ConnectorStatus)
